// components/Purchase/DiscountCodeModal.jsx

import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useVideo } from "../../context/VideoContext";
import {
  getUserInfoApi,
  checkDiscountCodeApi,
} from "../../services/paymentApi";
import {
  checkResponseData,
  checkErrorMessage,
} from "../../utils/responseHandler";
import { getPortalContainer } from "../../utils/portalContainer";
import "./DiscountCodeModal.css";

const DiscountCodeModal = ({
  isOpen,
  onClose,
  selectedPackage,
  onBack,
  onContinue,
  onUserInfoLoaded, // اضافه شد
}) => {
  const { isFullscreen: isVideoFullscreen, isIOS } = useVideo();

  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [checkingCode, setCheckingCode] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [error, setError] = useState("");

  // مبلغ اصلی و نهایی
  const originalPrice = selectedPackage?.packageId?.price || 0;
  const [finalPrice, setFinalPrice] = useState(originalPrice);

  // گرفتن اطلاعات کاربر در ابتدا
  useEffect(() => {
    if (isOpen) {
      fetchUserInfo();
    }
  }, [isOpen]);

  // بروزرسانی قیمت نهایی با تخفیف
  useEffect(() => {
    if (discountInfo?.Discount) {
      const discountAmount = (originalPrice * discountInfo.Discount) / 100;
      setFinalPrice(originalPrice - discountAmount);
    } else {
      setFinalPrice(originalPrice);
    }
  }, [discountInfo, originalPrice]);

  // گرفتن اطلاعات کاربر
  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await getUserInfoApi();
      const result = checkResponseData({ data: response });

      if (result.success && result.data?.data?.[0]) {
        const userData = result.data.data[0];
        setUserInfo(userData);
        onUserInfoLoaded(userData); // ارسال اطلاعات به والد

        if (userData.presenterCode) {
          setDiscountCode(userData.presenterCode);
        }
      }
    } catch (err) {
      console.error("خطا در دریافت اطلاعات کاربر:", err);
    } finally {
      setLoading(false);
    }
  };

  // بررسی کد تخفیف
  const handleCheckCode = async () => {
    if (!discountCode.trim()) {
      setError("لطفا کد تخفیف را وارد کنید");
      return;
    }

    try {
      setCheckingCode(true);
      setError("");
      setDiscountInfo(null);

      const response = await checkDiscountCodeApi(
        discountCode.trim(),
        selectedPackage.packageId._id,
      );

      const result = checkResponseData({ data: response });

      if (result.success && result.data?.data?.[0]) {
        setDiscountInfo(result.data.data[0]);
      } else {
        setError(response?.message || "کد تخفیف نامعتبر است");
      }
    } catch (err) {
      const errorDetails = checkErrorMessage(err);
      setError(errorDetails.message || "خطا در بررسی کد تخفیف");
    } finally {
      setCheckingCode(false);
    }
  };

  // ادامه به صفحه بعد
  const handleContinue = () => {
    onContinue({
      discountInfo,
      discountCode: discountCode.trim() || null,
      finalPrice,
    });
  };

  // پیدا کردن container مناسب برای Portal
  if (!isOpen) return null;

  const formatPrice = (price) => {
    const toman = price / 10;
    return toman.toLocaleString() + " تومان";
  };

  const modalContent = (
    <div className="discount-modal-overlay">
      <div className="discount-modal" onClick={(e) => e.stopPropagation()}>
        <div className="discount-modal-header">
          <button className="back-btn" onClick={onBack}>
            ← بازگشت
          </button>
          <h3>کد تخفیف</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="discount-modal-content">
          {loading ? (
            <div className="discount-loading">
              <div className="spinner"></div>
              <p>در حال دریافت اطلاعات...</p>
            </div>
          ) : (
            <>
              {/* اطلاعات پکیج */}
              <div className="package-summary">
                <div className="summary-title">
                  {selectedPackage.packageId.displayName}
                </div>
                <div className="summary-price">
                  <span className="price-label">قیمت:</span>
                  <span className="price-value">
                    {formatPrice(originalPrice)}
                  </span>
                </div>
              </div>

              {/* بخش کد تخفیف */}
              <div className="discount-section">
                <label>کد تخفیف</label>
                <div className="code-input-group">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value);
                      setDiscountInfo(null);
                      setError("");
                    }}
                    placeholder="کد تخفیف را وارد کنید"
                    disabled={checkingCode}
                  />
                  <button
                    onClick={handleCheckCode}
                    disabled={checkingCode || !discountCode.trim()}
                    className="check-btn"
                  >
                    {checkingCode ? "..." : "ثبت"}
                  </button>
                </div>

                {/* پیام خطا */}
                {error && <div className="discount-error">{error}</div>}

                {/* اطلاعات تخفیف */}
                {discountInfo && (
                  <div className="discount-info">
                    <div className="discount-badge">
                      {discountInfo.Discount}% تخفیف
                    </div>
                    {discountInfo.message && (
                      <p className="discount-message">{discountInfo.message}</p>
                    )}
                  </div>
                )}
              </div>

              {/* خلاصه قیمت نهایی */}
              <div className="price-summary">
                <div className="price-row">
                  <span>مبلغ اصلی:</span>
                  <span>{formatPrice(originalPrice)}</span>
                </div>

                {discountInfo && (
                  <div className="price-row discount">
                    <span>تخفیف ({discountInfo.Discount}%):</span>
                    <span>- {formatPrice(originalPrice - finalPrice)}</span>
                  </div>
                )}

                <div className="price-row total">
                  <span>مبلغ قابل پرداخت:</span>
                  <span className="final-amount">
                    {formatPrice(finalPrice)}
                  </span>
                </div>
              </div>

              {/* دکمه ادامه */}
              <button className="continue-btn" onClick={handleContinue}>
                ادامه و انتخاب درگاه
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const portalContainer = getPortalContainer(isIOS, isVideoFullscreen);
  return ReactDOM.createPortal(modalContent, portalContainer);
};

export default DiscountCodeModal;
